import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, ShieldCheck } from 'lucide-react';

interface TwoFactorVerifyProps {
  onVerified: () => void;
  onCancel?: () => void;
}

export function TwoFactorVerify({ onVerified, onCancel }: TwoFactorVerifyProps) {
  const [code, setCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const handleVerify = async () => {
    if (code.length !== 6) return;

    setIsVerifying(true);
    try {
      const { data: factors, error: factorsError } = await supabase.auth.mfa.listFactors();

      if (factorsError || !factors?.totp?.length) {
        toast.error('Δεν βρέθηκε ενεργός παράγοντας 2FA');
        return;
      }

      const totpFactor = factors.totp[0];

      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: totpFactor.id,
      });

      if (challengeError) {
        toast.error(`Σφάλμα: ${challengeError.message}`);
        return;
      }

      const { error: verifyError } = await supabase.auth.mfa.verify({
        factorId: totpFactor.id,
        challengeId: challengeData.id,
        code,
      });

      if (verifyError) {
        toast.error('Λάθος κωδικός. Δοκιμάστε ξανά.');
        setCode('');
        return;
      }

      onVerified();
    } catch {
      toast.error('Σφάλμα κατά την επαλήθευση');
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="space-y-6 text-center">
      <div className="w-16 h-16 mx-auto bg-primary/10 rounded-full flex items-center justify-center">
        <ShieldCheck className="w-8 h-8 text-primary" />
      </div>

      <div>
        <h3 className="text-lg font-semibold">Έλεγχος Ταυτότητας 2FA</h3>
        <p className="text-sm text-muted-foreground mt-1">
          Εισάγετε τον 6ψήφιο κωδικό από την εφαρμογή αυθεντικοποίησης
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="mfaCode" className="sr-only">Κωδικός 2FA</Label>
        <Input
          id="mfaCode"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
          onKeyDown={(e) => e.key === 'Enter' && code.length === 6 && handleVerify()}
          className="text-center tracking-[0.5em] font-mono text-lg"
          autoFocus
        />
      </div>

      <Button
        onClick={handleVerify}
        disabled={isVerifying || code.length !== 6}
        className="w-full"
      >
        {isVerifying ? (
          <>
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            Επαλήθευση...
          </>
        ) : (
          'Επαλήθευση'
        )}
      </Button>

      {onCancel && (
        <Button variant="ghost" onClick={onCancel} className="w-full">
          Ακύρωση
        </Button>
      )}
    </div>
  );
}
